import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Logo, SiteLink, WhatsAppCta } from "@/components/brand";
import { CTA_LABEL_SHORT } from "@/lib/offer";

export const navLinks = [
  { label: "Cases", href: "/#cases" },
  { label: "Projetos", href: "/projetos" },
  { label: "Como funciona", href: "/#processo" },
  { label: "Preços", href: "/#precos" },
  { label: "FAQ", href: "/#faq" },
];

const linkId = (href) => href.replace(/^\/#?/, "");

// Tracks which nav section sits under the middle band of the viewport.
// `pathname` re-attaches the observer when the home sections mount again.
function useActiveSection(ids, pathname) {
  const [active, setActive] = useState(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => {
      observer.disconnect();
      setActive(null);
    };
  }, [ids, pathname]);
  return active;
}

const sectionIds = navLinks.filter((link) => link.href.startsWith("/#")).map((link) => linkId(link.href));

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const { pathname } = useLocation();
  const activeSection = useActiveSection(sectionIds, pathname);
  const active = pathname === "/" ? activeSection : linkId(pathname);

  useEffect(() => setMobileOpen(false), [pathname]);

  useMotionValueEvent(scrollY, "change", (y) => setScrolled(y > 24));

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b bg-background/85 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 ${
        scrolled
          ? "border-border/80 shadow-[0_10px_30px_-20px_rgba(17,26,51,0.35)]"
          : "border-transparent"
      }`}
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between px-4 transition-[height] duration-300 sm:px-6 lg:px-8 ${
          scrolled ? "h-14" : "h-16"
        }`}
      >
        <Logo />

        <div className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) => {
            const isActive = active === linkId(link.href);
            return (
              <SiteLink
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${linkId(link.href)}`}
                aria-current={isActive ? "true" : undefined}
                className={`relative py-1 text-sm font-medium transition-colors hover:text-foreground ${
                  isActive ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {link.label}
                {isActive && (
                  <motion.span
                    layoutId="nav-active"
                    transition={{ type: "spring", stiffness: 400, damping: 34 }}
                    className="absolute inset-x-0 -bottom-0.5 h-0.5 rounded-full bg-accent"
                  />
                )}
              </SiteLink>
            );
          })}
          <WhatsAppCta size="sm" label={CTA_LABEL_SHORT} location="navbar" data-testid="navbar-cta" />
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((open) => !open)}
          className="-mr-2 p-2 text-foreground md:hidden"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-border bg-background md:hidden"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {navLinks.map((link) => (
                <SiteLink
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 text-base font-medium text-foreground"
                >
                  {link.label}
                </SiteLink>
              ))}
              <WhatsAppCta
                location="mobile-menu"
                className="mt-3 w-full"
                onClick={() => setMobileOpen(false)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
